import { createFolderAlreadyExistsDescription } from '../../strings.locstring.json';
import { somethingWentWrongLabel } from 'owa-locstrings/lib/strings/somethingwentwronglabel.locstring.json';
import loc, { format } from 'owa-localize';
import type { Dispatch } from 'redux';
import {
    createActionFactory,
    Feature,
    addNotification,
    FolderGroupType,
    createThunk,
    ThunkDispatch,
    ContactFolder,
} from '../../../shared';
import {
    createFolder,
    deleteFolder,
    renameFolder,
    moveFolder,
    hardDeleteFolder,
    removeFavoritePublicFolder,
} from '../data/folderManagementDataActions';
import type { CreateFolderExpectedError } from '../../dataLayer/createFolder';
import { getAppState } from '../../../appstate';

import { lazyLoadPublicFolders } from 'owa-publicfolder-picker';

export const addPublicFolderClickedAction = createActionFactory<void>(
    'AddPublicFolderClickedAction',
    Feature.FolderManagement
);

export const removePublicFolderClickedAction = createActionFactory<{ folderId: string }>(
    'RemovePublicFolderClickedAction',
    Feature.FolderManagement
);

export const addPublicFolderToFavoritesCompletedAction = createActionFactory<{
    folder: ContactFolder;
}>('AddPublicFolderToFavoritesCompletedAction', Feature.FolderManagement);

export const newFolderEnteredAction = createActionFactory<{ name: string }>(
    'NewFolderEnteredAction',
    Feature.FolderManagement
);

export const renameFolderEnteredAction = createActionFactory<{ folderId: string; newName: string }>(
    'RenameFolderEnteredAction',
    Feature.FolderManagement
);

export const moveFolderClickedAction = createActionFactory<{ folderId: string }>(
    'MoveFolderEnteredAction',
    Feature.FolderManagement
);

export const deleteFolderClickedAction = createActionFactory<{ folderId: string }>(
    'DeleteFolderClickedAction',
    Feature.FolderManagement
);
export const confirmDeleteFolderClickedAction = createActionFactory<{ folderId: string }>(
    'ConfirmDeleteFolderClickedAction',
    Feature.FolderManagement
);
export const cancelDeleteFolderClickedAction = createActionFactory<void>(
    'CancelDeleteFolderClickedAction',
    Feature.FolderManagement
);

export const hardDeleteFolderClickedAction = createActionFactory<{ folderId: string }>(
    'HardDeleteFolderClickedAction',
    Feature.FolderManagement
);
export const confirmHardDeleteFolderClickedAction = createActionFactory<{ folderId: string }>(
    'ConfirmHardDeleteFolderClickedAction',
    Feature.FolderManagement
);
export const cancelHardDeleteFolderClickedAction = createActionFactory<void>(
    'CancelHardDeleteFolderClickedAction',
    Feature.FolderManagement
);

export const addPublicFolderClicked = (dispatch: Dispatch) => async () => {
    dispatch(addPublicFolderClickedAction());
    const loadPublicFolders = await lazyLoadPublicFolders.import();
    loadPublicFolders('publicfoldersroot');
};

export const removePublicFolderClicked = (dispatch: Dispatch) => (
    folderId: string,
    folderName: string
) => {
    dispatch(removePublicFolderClickedAction({ folderId }));
    removeFavoritePublicFolder(dispatch)(folderId, folderName);
};

export const newFolderEntered = (dispatch: Dispatch) => (
    name: string,
    rootFolderId: string,
    skipErrorIfExists?: boolean
): Promise<ContactFolder | CreateFolderExpectedError | void> => {
    dispatch(newFolderEnteredAction({ name }));
    return createFolder(dispatch)(name, rootFolderId, skipErrorIfExists);
};

export const moveFolderClicked = (dispatch: ThunkDispatch) => (
    folderId: string,
    toFolderId: string
) => {
    dispatch(moveFolderClickedAction({ folderId }));
    dispatch(moveFolder(folderId, toFolderId));
};

export const renameFolderEntered = (folderId: string, newName: string) =>
    createThunk('RenameFolderEntered', (dispatch: ThunkDispatch, getState: () => {}) => {
        const newNameLowerCase = newName.toLowerCase();
        const state = getAppState(getState());

        const userFolders = state.data.contactFoldersState.displayedContactFolders.filter(
            folder =>
                folder.group === FolderGroupType.userCreatedFolder ||
                folder.group === FolderGroupType.otherFolder
        );
        const currentFolder = userFolders.find(folder => folder.id === folderId);

        if (currentFolder.displayName.toLowerCase() === newNameLowerCase) {
            // No change - do nothing
            return;
        } else if (
            userFolders.some(
                folder =>
                    folder.id !== folderId && folder.displayName.toLowerCase() === newNameLowerCase
            )
        ) {
            // Different user created or other folder with the same name already exists
            addNotification(
                loc(somethingWentWrongLabel),
                format(loc(createFolderAlreadyExistsDescription), newName)
            );
        } else {
            dispatch(renameFolderEnteredAction({ folderId, newName }));
            renameFolder(dispatch)(folderId, newName, currentFolder.displayName);
        }
    });

export const deleteFolderClicked = (dispatch: Dispatch) => (folderId: string) => {
    dispatch(deleteFolderClickedAction({ folderId }));
};

export const confirmDeleteFolderClicked = (dispatch: ThunkDispatch) => (
    folderId: string,
    folderName: string
) => {
    dispatch(confirmDeleteFolderClickedAction({ folderId }));
    dispatch(deleteFolder(folderId, folderName));
};

export const cancelDeleteFolderClicked = (dispatch: Dispatch) => () =>
    dispatch(cancelDeleteFolderClickedAction());

export const hardDeleteFolderClicked = (dispatch: Dispatch) => (folderId: string) => {
    dispatch(hardDeleteFolderClickedAction({ folderId }));
};

export const confirmHardDeleteFolderClicked = (dispatch: ThunkDispatch) => (folderId: string) => {
    dispatch(confirmHardDeleteFolderClickedAction({ folderId }));
    dispatch(hardDeleteFolder(folderId));
};

export const cancelHardDeleteFolderClicked = (dispatch: Dispatch) => () =>
    dispatch(cancelHardDeleteFolderClickedAction());
export type { Action } from '../../../shared';
